import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Check, ChevronsUpDown } from 'lucide-react';
import { DURATION_MIN_SECS, DURATION_MAX_SECS } from './pipelineUtils';

export function TechQuestionMeta({ topic, difficulty }) {
  if (!topic && !difficulty) return null;
  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-1">
      {topic ? (
        <span className="rounded-full border bg-card px-1.5 py-0.5 text-[10px] text-muted-foreground">{topic}</span>
      ) : null}
      {difficulty ? (
        <span className="rounded-full border bg-secondary px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{difficulty}</span>
      ) : null}
    </span>
  );
}

/** Compact multi-select for pipeline question filters (category, difficulty). */
export function PipelineFilterMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder,
  disabled = false,
  testId,
  compact = false,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = Array.isArray(value) ? value : [];
  const byValue = React.useMemo(() => {
    const m = new Map();
    for (const o of options) m.set(o.value, o);
    return m;
  }, [options]);

  const toggle = (v) => {
    if (disabled) return;
    onChange?.(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (byValue.get(selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={compact ? 'h-7 justify-between px-2 text-[11px] font-normal' : 'h-9 justify-between font-normal'}
          data-testid={testId}
        >
          <span className={`truncate ${selected.length === 0 ? 'text-muted-foreground' : ''}`}>{label}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="max-h-56 overflow-y-auto p-1" data-testid={testId ? `${testId}-list` : undefined}>
          {options.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">No options</p>
          ) : (
            options.map((o) => {
              const checked = selected.includes(o.value);
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={checked}
                  tabIndex={disabled ? -1 : 0}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onClick={() => toggle(o.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(o.value);
                    }
                  }}
                  data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                >
                  <span
                    aria-hidden
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-primary shadow ${checked ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                  >
                    {checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                  </span>
                  <span className="flex-1 truncate">{o.label}</span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function clampDurationSeconds(total) {
  const n = Number(total);
  if (!Number.isFinite(n)) return DURATION_MIN_SECS;
  return Math.min(DURATION_MAX_SECS, Math.max(DURATION_MIN_SECS, Math.round(n)));
}

/** Minimal minutes + seconds editor; value is total seconds (API range 60–14400). */
export function DurationMinsSecs({ value, onChange, testIdPrefix }) {
  const total = clampDurationSeconds(value ?? DURATION_MIN_SECS);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  const setFromParts = (nextMins, nextSecs) => {
    const m = Math.max(0, Math.floor(Number(nextMins) || 0));
    let s = Math.floor(Number(nextSecs) || 0);
    if (s < 0) s = 0;
    if (s > 59) s = 59;
    onChange(clampDurationSeconds(m * 60 + s));
  };
  return (
    <div className="flex items-center gap-1.5" data-testid={testIdPrefix ? `${testIdPrefix}-duration` : undefined}>
      <label className="text-[11px] text-muted-foreground">Duration</label>
      <Input
        type="number"
        min={0}
        max={240}
        value={mins}
        onChange={(e) => setFromParts(e.target.value, secs)}
        className="h-7 w-16 px-2 text-xs tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        data-testid={testIdPrefix ? `${testIdPrefix}-mins` : undefined}
        aria-label="Minutes"
      />
      <span className="text-[11px] text-muted-foreground">min</span>
      <Input
        type="number"
        min={0}
        max={59}
        value={secs}
        onChange={(e) => setFromParts(mins, e.target.value)}
        className="h-7 w-16 px-2 text-xs tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        data-testid={testIdPrefix ? `${testIdPrefix}-secs` : undefined}
        aria-label="Seconds"
      />
      <span className="text-[11px] text-muted-foreground">sec</span>
      <input type="hidden" data-testid={testIdPrefix ? `${testIdPrefix}-total-seconds` : undefined} value={total} readOnly />
    </div>
  );
}

export function SelectRandomQuestionsDialog({
  open,
  onOpenChange,
  title,
  description,
  count,
  setCount,
  maxCount,
  onConfirm,
  loading = false,
  testIdPrefix,
}) {
  const parsedCount = Number(count);
  const countIsInt = Number.isInteger(parsedCount);
  const exceedsMax = countIsInt && maxCount >= 1 && parsedCount > maxCount;
  const countValid = countIsInt && parsedCount >= 1 && parsedCount <= maxCount;
  const confirmDisabled = loading || maxCount < 1 || !countValid;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next); }}>
      <DialogContent data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="block text-sm font-medium" htmlFor={`${testIdPrefix}-count`}>
            Number of questions
          </label>
          <Input
            id={`${testIdPrefix}-count`}
            type="number"
            min={1}
            max={Math.max(1, maxCount || 1)}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            disabled={loading || maxCount < 1}
            data-testid={`${testIdPrefix}-count`}
          />
          {exceedsMax ? (
            <p className="text-xs text-amber-700" data-testid={`${testIdPrefix}-over-limit`}>
              You asked for {parsedCount}, but only {maxCount} eligible question{maxCount === 1 ? '' : 's'} {maxCount === 1 ? 'is' : 'are'} available.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Up to {maxCount} eligible active question{maxCount === 1 ? '' : 's'} can be selected.
              The result fills this draft only; click Save binding to lock it.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {loading ? 'Selecting…' : 'Select questions'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
